import { Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { AsgardFeatureNames, featureIcons } from 'src/models/AsgardFeatures';
import { IFeatureRestrictionPreset } from 'src/models/FeatureRestrictionPreset';
import { CurrentUIState } from 'src/models/UIState';
import { CurrentUiStoreService } from '../ui/current-ui-store.service';

import { HttpClient } from '@angular/common/http';
import { AuthService } from '../auth/auth.service';
import { environment } from 'src/environments/environment';
import { ToastrDialogService } from '../toastr-dialog.service';
import { RequestTypeEnum, ToastrActionEnum, ToastrStringsEnum } from 'src/app/utils/enums';
import { SettingsService } from '../settings/settings.service';
import { BaseApiService } from '../auth/base-api.service';

export class AsgardFeaturesRestrictionItem {
  name: AsgardFeatureNames;
  icon: string;
  isLocked: boolean;
  isSubFeature: boolean;
  tooltip: string;
}

@Injectable({
  providedIn: 'root'
})
export class FeatureRestrictionService extends BaseApiService implements OnDestroy {
  // features preset for exam window
  preset: IFeatureRestrictionPreset | undefined;

  private readonly subscriptions: Subscription = new Subscription();

  private featuresThatCanBeBlocked: AsgardFeatureNames[] = [
    AsgardFeatureNames.AUDIO_MAKER,
    AsgardFeatureNames.WORD_PREDICTION,
    AsgardFeatureNames.SPEECH, // Reading
    AsgardFeatureNames.SPELL_CHECK,
    AsgardFeatureNames.MARKUP_MENU, // Selection modes
    AsgardFeatureNames.SCREENSHOT_READER,
    AsgardFeatureNames.LOOK_UP,
    AsgardFeatureNames.SPEECH_RECOGNITION,
    AsgardFeatureNames.DICTIONARY,
    AsgardFeatureNames.TOOLBAR_MENU, // Notes
    AsgardFeatureNames.IMAGE_DICTIONARY,
    AsgardFeatureNames.DRAWING_TOOLS,
    AsgardFeatureNames.CONJUGATIONS,
    AsgardFeatureNames.TRANSLATE,
    AsgardFeatureNames.HIGHLIGHT_MENU, // Markers
  ];
  get featuresThatCanBeBlockedExam(): AsgardFeatureNames[] {
    return this.featuresThatCanBeBlocked;
  }

  // all features, isLocked false
  private features: AsgardFeaturesRestrictionItem[];
  // to be used in the settings window
  private settingsFeatures: AsgardFeaturesRestrictionItem[];

  public editingCustom: boolean = false;
  public readonly defaultBlockedFeatures: AsgardFeatureNames[] = [
    AsgardFeatureNames.AUDIO_MAKER, 
    AsgardFeatureNames.TRANSLATE
  ];

  get baseUrl(): string {
    return environment.connectedApiUrls.woland + '/' + environment.apiVersions.woland;
  }

  constructor(
    public http: HttpClient,
    private readonly uiStore: CurrentUiStoreService,
    private readonly settingsService: SettingsService,
    private authService: AuthService,
    private toastrService: ToastrDialogService,
  ) {
    super(http);
    this.initSubscriptions();
    this.makeFeatureList();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getFeatures(): AsgardFeaturesRestrictionItem[] {
    return this.features;
  }

  lockFeature(name: AsgardFeatureNames): void {
    this.uiStore.setItemLocked(name);
    this.uiStore.setItemUnActive(name);
  }

  unlockFeature(name: AsgardFeatureNames): void {
    this.uiStore.setItemUnlocked(name);
  }

  setPreset(preset?: IFeatureRestrictionPreset) {
    if (preset) {
      this.editingCustom = false;
    }
    this.preset = preset;
    this.saveSelectedPreset(preset);
  }

  reset(): void {
    this.preset = undefined;
  }

  saveSelectedPreset(preset?: IFeatureRestrictionPreset) {
    if (!preset) {
      return;
    }

    const settings = this.settingsService.settings;
    settings.examLastRestrictionTemplate = preset.name;

    this.settingsService.saveSettings(settings);
  }

  getLastSelectedPreset(): string | undefined {
    const settings = this.settingsService.settings;
    return this.editingCustom ? undefined : settings.examLastRestrictionTemplate;
  }

  goingCustom() {
    this.editingCustom = true;
    // this.customFeatures = features;
  }

  getAvailableFeatures(blockedFeatures: AsgardFeatureNames[]): AsgardFeatureNames[] {
    return this.featuresThatCanBeBlocked.filter(x => !blockedFeatures.includes(x));
  }

  async isMyPreset(preset: IFeatureRestrictionPreset) {
    return await this.authService.isSameUser(preset.userId === undefined ? '' : preset.userId);
  }

  async saveFeatureRestrictionsTemplate(features: object): Promise<void> {
    const url = this.baseUrl + '/featureRestrictions/SaveFeatureRestrictionTemplate';
    return this.post<void>(url, features, undefined, RequestTypeEnum.FeatureRestrictionTemplateAdd)
      .then(() => {
        this.toastrService.showToastrByType(ToastrActionEnum.Success, ToastrStringsEnum.FeatureTemplateSaveSuccess, false);
        this.editingCustom = false;
      });

    // return (this.http.post(environment.connectedApiUrls.woland + '/' + environment.apiVersions.woland + '/featureRestrictions/SaveFeatureRestrictionTemplate', features, { headers: headers })
    //   .toPromise()
    //   .then(() => {
    //     this.toastrService.showToastrByType(ToastrActionEnum.Success, ToastrStringsEnum.FeatureTemplateSaveSuccess, false);
    //     this.editingCustom = false;
    //   })
    //   .catch((err) => {
    //     this.toastrService.showToastrByType(ToastrActionEnum.Error, ToastrStringsEnum.FeatureTemplateSaveError, false);
    //     console.log(err);
    //   })) as Promise<void>;
  }

  async updateFeatureRestrictionsTemplate(features: object): Promise<void> {
    const url = this.baseUrl + '/featureRestrictions/UpdateFeatureRestrictionTemplate';

    return this.post<void>(url, features, undefined, RequestTypeEnum.FeatureRestrictionTemplateUpdate)
      .then(() => {
        this.toastrService.showToastrByType(ToastrActionEnum.Success, ToastrStringsEnum.FeatureTemplateSaveSuccess, false);
      });

    // return (this.http.post(environment.connectedApiUrls.woland + '/' + environment.apiVersions.woland + '/featureRestrictions/UpdateFeatureRestrictionTemplate', features, { headers: headers })
    //   .toPromise()
    //   .then(() => {
    //     this.toastrService.showToastrByType(ToastrActionEnum.Success, ToastrStringsEnum.FeatureTemplateSaveSuccess, true);
    //   })
    //   .catch((err) => {
    //     console.log(err);
    //     this.toastrService.showToastrByType(ToastrActionEnum.Error, ToastrStringsEnum.FeatureTemplateSaveError, false);
    //   })) as Promise<void>;
  }

  async getFeatureRestrictionsTemplateList(): Promise<IFeatureRestrictionPreset[] | null> {
    const url = this.baseUrl + '/featureRestrictions/GetListFeatureRestrictionTemplates';

    return this.get<IFeatureRestrictionPreset[]>(url, undefined, RequestTypeEnum.FeatureRestrictionTemplateList);

    // return (this.http.get(environment.connectedApiUrls.woland + '/' + environment.apiVersions.woland + '/featureRestrictions/GetListFeatureRestrictionTemplates', { headers: headers })
    //   .toPromise()
    //   .catch((err) => {
    //     console.log(err);
    //     throw err;
    //   })) as Promise<IFeatureRestrictionPreset[]>;
  }

  async getFeatureRestrictionsTemplate(id: number): Promise<IFeatureRestrictionPreset | null> {
    const url = this.baseUrl + '/featureRestrictions/GetFeatureRestrictionTemplate/' + id;

    return this.get<IFeatureRestrictionPreset>(url, undefined, RequestTypeEnum.FeatureRestrictionTemplate);

    // return (this.http.get(environment.connectedApiUrls.woland + '/' + environment.apiVersions.woland + '/featureRestrictions/GetFeatureRestrictionTemplate/' + id, { headers: headers })
    //   .toPromise()
    //   .catch((err) => {
    //     console.log(err);
    //   })) as Promise<IFeatureRestrictionPreset>;
  }

  async deleteTemplate(id: number): Promise<void> {
    const url = this.baseUrl + '/featureRestrictions/' + id;

    return this.delete<void>(url, undefined, RequestTypeEnum.FeatureRestrictionTemplateDelete);
    // return (this.http.delete(environment.connectedApiUrls.woland + '/' + environment.apiVersions.woland + '/featureRestrictions/' + id, { headers: headers })
    //   .toPromise()
    //   .catch((err) => {
    //     console.log(err);
    //     this.toastrService.showToastrByType(ToastrActionEnum.Error, ToastrStringsEnum.FeatureTemplateDeleteError, false);
    //   })) as Promise<void>;
  }

  private initSubscriptions(): void {
    this.subscriptions.add(
      this.uiStore.getUIState().subscribe(e => {
        if (e) {
          this.setLockedStateForSettings(e);
        }
      })
    );
  }

  private makeFeatureList(): void {
    // Copy the list and remove translate and audiomaker feature
    const filteredFeatures = this.featuresThatCanBeBlocked.slice();
    for (const feature of this.defaultBlockedFeatures) {
      const index = filteredFeatures.indexOf(feature);
      filteredFeatures.splice(index, 1);
    }

    // create the feature list of the copied list
    this.features = filteredFeatures.map((feature) => {
      const item = new AsgardFeaturesRestrictionItem();
      item.name = feature;
      item.isLocked = false;
      item.icon = featureIcons.get(feature) ?? '';

      if (feature === AsgardFeatureNames.DICTIONARY
        || feature === AsgardFeatureNames.IMAGE_DICTIONARY
        || feature === AsgardFeatureNames.CONJUGATIONS
      ) {
        item.isSubFeature = true;
      }

      item.tooltip = 'EXAM_PROPERTIES_WINDOW.FEATURE_RESTRICTIONS.' + feature;
      return item;
    });
  }

  private setLockedStateForSettings(e: CurrentUIState): void {
    if (this.settingsFeatures) {
      this.settingsFeatures.forEach(feature => {
        feature.isLocked = e[feature.name].locked;
      });
    }
  }
}
